import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { Edit as EditIcon, Visibility as ViewIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import deliveryService from '../../../services/deliveryService';
import AppDataGrid from '../../common/AppDataGrid';
import GridActionsCell from '../../common/GridActionsCell';
import TableSearch from '../../common/TableSearch';
import { useCanUpdate } from '../../common/activeToggle';
import { useDataTable } from '../../../hooks/useDataTable';
import { useCurrency } from '../../../hooks/useCurrency';
import { useDeliveryCompanies } from '../../../hooks/useDeliveryCompanies';

// All 58 wilayas fit on one page by default (/delivery?page=0&size=58); the user can still paginate.
const DEFAULT_PARAMS = { size: 58 };

const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR') : '—');

const DetailRow = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, gap: 2 }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={500} sx={{ textAlign: 'right' }}>
      {value}
    </Typography>
  </Box>
);

const toInput = (value) => (value === null || value === undefined ? '' : String(value));

// A row's pickup price for one company (0 = not configured).
const companyPriceOf = (row, companyId) =>
  row?.companyPrices?.find((p) => p.companyId === companyId)?.price ?? 0;

const DeliveryList = () => {
  const { t } = useTranslation();
  const { format, currencySymbol } = useCurrency();
  const canEdit = useCanUpdate('stores.update');
  // Price columns / fields are built from the active companies (managed on the companies page).
  const { companies } = useDeliveryCompanies({ activeOnly: true });

  const [detailsRow, setDetailsRow] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [prices, setPrices] = useState({ homeDeliveryPrice: '' }); // + one entry per company id
  const [saving, setSaving] = useState(false);

  // Page, size and search live in the URL (/delivery?page=0&size=58&search=).
  const table = useDataTable(deliveryService.getDeliveryPage, DEFAULT_PARAMS, {
    onError: () => toast.error(t('delivery.fetchError')),
  });

  const openEdit = (row) => {
    setEditRow(row);
    const next = { homeDeliveryPrice: toInput(row.homeDeliveryPrice) };
    companies.forEach((c) => {
      next[c.id] = toInput(companyPriceOf(row, c.id) || '');
    });
    setPrices(next);
  };

  // Empty = 0; anything else must be a finite number >= 0.
  const parsePrice = (raw) => {
    if (raw === '' || raw === null) return 0;
    const n = Number(String(raw).replace(',', '.'));
    return Number.isFinite(n) && n >= 0 ? n : NaN;
  };

  const parsed = { homeDeliveryPrice: parsePrice(prices.homeDeliveryPrice) };
  companies.forEach((c) => {
    parsed[c.id] = parsePrice(prices[c.id]);
  });
  const invalid = (key) => Number.isNaN(parsed[key]);
  const hasError = Object.keys(parsed).some(invalid);

  const handleSave = async () => {
    if (hasError) return;
    setSaving(true);
    try {
      const updated = await deliveryService.updatePrices(editRow.id, {
        homeDeliveryPrice: parsed.homeDeliveryPrice,
        companyPrices: companies.map((c) => ({ companyId: c.id, price: parsed[c.id] })),
      });
      // Update just that row in place — no reload of the table.
      table.setData((prev) => ({
        ...prev,
        content: prev.content.map((r) => (r.id === updated.id ? updated : r)),
      }));
      toast.success(t('delivery.saveSuccess'));
      setEditRow(null);
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const priceColumn = (field, headerName) => ({
    field,
    headerName,
    width: 140,
    cellRenderer: (params) => format(params.value ?? 0),
    tooltipValueGetter: (params) => format(params.value ?? 0),
  });

  // One column per active company; the value comes from the row's companyPrices (not sortable server-side).
  const companyColumn = (company) => ({
    colId: `company-${company.id}`,
    headerName: company.name,
    width: 140,
    sortable: false,
    valueGetter: (params) => companyPriceOf(params.data, company.id),
    cellRenderer: (params) => format(params.value ?? 0),
    tooltipValueGetter: (params) => format(params.value ?? 0),
  });

  const columns = [
    { field: 'wilayaCode', headerName: t('delivery.code'), width: 100 },
    { field: 'wilayaNameFr', headerName: t('delivery.wilayaFr'), flex: 1, minWidth: 180 },
    {
      field: 'wilayaNameAr',
      headerName: t('delivery.wilayaAr'),
      flex: 1,
      minWidth: 160,
      cellStyle: { direction: 'rtl', textAlign: 'right' },
    },
    ...companies.map(companyColumn),
    priceColumn('homeDeliveryPrice', t('delivery.homeDelivery')),
    {
      field: 'actions',
      headerName: t('common.actions'),
      width: 120,
      sortable: false,
      cellRenderer: (params) => (
        <GridActionsCell
          actions={[
            {
              key: 'details',
              icon: ViewIcon,
              title: t('delivery.details'),
              color: 'info',
              onClick: () => setDetailsRow(params.data),
            },
            {
              key: 'prices',
              icon: EditIcon,
              title: t('delivery.editPrices'),
              color: 'primary',
              hidden: !canEdit,
              onClick: () => openEdit(params.data),
            },
          ]}
        />
      ),
    },
  ];

  const priceField = (key, label) => (
    <TextField
      key={key}
      fullWidth
      label={label}
      type="number"
      value={prices[key]}
      onChange={(e) => setPrices((p) => ({ ...p, [key]: e.target.value }))}
      error={invalid(key)}
      helperText={invalid(key) ? t('delivery.invalidPrice') : ' '}
      InputProps={{ endAdornment: <InputAdornment position="end">{currencySymbol}</InputAdornment> }}
      inputProps={{ min: 0, step: 'any' }}
    />
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">{t('delivery.title')}</Typography>
      </Box>

      <Card>
        <CardContent>
          <TableSearch
            placeholder={t('delivery.searchPlaceholder')}
            value={table.searchInput}
            onChange={table.handleSearchChange}
            sx={{ mb: 2 }}
          />

          <AppDataGrid
            rows={table.data.content}
            columns={columns}
            loading={table.loading}
            paginationMode="server"
            rowCount={table.data.totalElements}
            paginationModel={table.paginationModel}
            onPaginationModelChange={table.handlePaginationModelChange}
            sortModel={table.sortModel}
            onSortModelChange={table.handleSortChange}
            pageSizeOptions={[10, 20, 58, 100]}
            getRowId={(row) => row.id}
          />
        </CardContent>
      </Card>

      {/* Read-only details */}
      <Dialog open={Boolean(detailsRow)} onClose={() => setDetailsRow(null)} fullWidth maxWidth="xs">
        {detailsRow && (
          <>
            <DialogTitle>
              {t('delivery.detailsTitle', { code: detailsRow.wilayaCode, name: detailsRow.wilayaNameFr })}
              <Typography variant="body2" color="text.secondary" sx={{ direction: 'rtl', textAlign: 'right' }}>
                {detailsRow.wilayaNameAr}
              </Typography>
            </DialogTitle>
            <Divider />
            <DialogContent>
              <DetailRow label={t('delivery.wilayaCode')} value={detailsRow.wilayaCode} />
              <DetailRow label={t('delivery.nameFr')} value={detailsRow.wilayaNameFr} />
              <DetailRow
                label={t('delivery.nameAr')}
                value={<span dir="rtl">{detailsRow.wilayaNameAr}</span>}
              />
              {companies.map((c) => (
                <DetailRow key={c.id} label={c.name} value={format(companyPriceOf(detailsRow, c.id))} />
              ))}
              <DetailRow label={t('delivery.homeDelivery')} value={format(detailsRow.homeDeliveryPrice ?? 0)} />
              <DetailRow label={t('delivery.lastUpdate')} value={formatDate(detailsRow.updatedAt)} />
              {detailsRow.isActive === false && (
                <Chip label={t('common.inactive')} size="small" sx={{ mt: 1 }} />
              )}
            </DialogContent>
            <Divider />
            <DialogActions>
              <Button onClick={() => setDetailsRow(null)}>{t('common.close')}</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Edit the home price + one price per active company */}
      <Dialog open={Boolean(editRow)} onClose={() => !saving && setEditRow(null)} fullWidth maxWidth="xs">
        {editRow && (
          <>
            <DialogTitle>
              {t('delivery.editTitle', { name: editRow.wilayaNameFr, code: editRow.wilayaCode })}
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1, pt: 3 }}>
              {companies.map((c) => priceField(c.id, c.name))}
              {priceField('homeDeliveryPrice', t('delivery.homeLabel'))}
            </DialogContent>
            <Divider />
            <DialogActions>
              <Button onClick={() => setEditRow(null)} disabled={saving}>
                {t('common.cancel')}
              </Button>
              <Button onClick={handleSave} variant="contained" disabled={saving || hasError}>
                {t('common.save')}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default DeliveryList;
